import React, { useContext, useMemo, useState } from 'react'
import { useField, useFormikContext } from 'formik'

import { StepContainer } from '../StepContainer'
import { BrandCardList, IBrand } from '../steps/Brand/BrandCardList'
import { Confirm } from '../steps/Confirm/Confirm'
import { IModel, IVehicleAttribute } from '@cbgms/api'
import { StepOption, useSelectionContext } from '../../context/SelectionContext'
import { ModelChips } from '../steps/Model/ModelChips'
import { useGetMDMDetails } from './hooks/useGetMDMDetails'
import { EmptyDate } from '@cbgms/base/utils/datetime'
import { FormControl, InputLabel, MenuItem, Select, Skeleton } from '@mui/material'
import { loadVehicleDetails } from 'app/vehicles/state/actions'
import { useDispatch } from 'react-redux'
import { LocaleContext } from '@cbgms/base/locale/LocaleContext'
import BrandLoader from '../steps/Loader/BrandLoader'
import { ModelChipsLoader } from '../steps/Loader/ModelLoader'
import { TypeSelectLoader } from '../steps/Loader/TypeLoader'
import { useShopCountry } from '@cbgms/base/shop-country'

export const MDM: React.FC<{ slotBottomButtons?: React.ReactNode }> = ({ slotBottomButtons }) => {
  const { currentStep, setBrand, brand, setModel, model } = useSelectionContext()

  const { setFieldValue } = useFormikContext()
  const { country } = useShopCountry()

  const { data: mdmBrands, isFetching: isLoadingBrands } = useGetMDMDetails(country!, true, [])
  const selectedBrand = useMemo(
    () => mdmBrands?.Attributes.find(attr => attr.Name === 'Make' && attr.Value === brand?.key),
    [mdmBrands, brand]
  )
  const { data: mdmModels, isFetching: isLoadingModels } = useGetMDMDetails(country!, !!selectedBrand, [selectedBrand!])
  const selectedModel = useMemo(
    () => mdmModels?.Attributes.find(attr => attr.Name === 'Model' && attr.Value === model?.key),
    [mdmModels, model]
  )

  const { brands, models } = useMemo(() => {
    return {
      brands: mdmBrands?.Attributes.filter(attr => attr.Name === 'Make').map(attr => ({
        key: attr.Value,
        value: attr.Value,
        name: attr.DisplayValue
      })),
      models: mdmModels?.Attributes.filter(attr => attr.Name === 'Model').map(attr => ({
        ModelID: attr.Value,
        ModelName: attr.DisplayValue,
        Remarks: '',
        MadeFrom: EmptyDate,
        MadeUntil: EmptyDate
      }))
    }
  }, [mdmBrands, mdmModels])

  const onBrandSelect = (key: string, brand: IBrand) => {
    setBrand({ key, value: brand.value, name: brand.name })
    setFieldValue('vehicle', { Manufacturer: brand.name, ManufacturerID: key })
  }
  const onModelSelect = (key: string, model: IModel) => {
    setModel({ key, value: model, name: model.ModelName })
    setFieldValue('vehicle', {
      Manufacturer: brand?.name,
      ManufacturerID: brand?.key,
      Model: model.ModelName,
      ModelID: key,
      MDMAttributes: null
    })
  }

  return (
    <StepContainer>
      <SelectionSteps
        currentStep={currentStep}
        onBrandSelect={onBrandSelect}
        onModelSelect={onModelSelect}
        brands={brands!}
        models={models!}
        attributes={[selectedBrand!, selectedModel!]}
        isLoading={isLoadingBrands || isLoadingModels}
        slotBottomButtons={slotBottomButtons}
      />
    </StepContainer>
  )
}

interface SelectionStepProps {
  currentStep: StepOption
  brands: IBrand[]
  models: IModel[]
  onBrandSelect: (key: string, value: IBrand) => void
  onModelSelect: (key: string, value: IModel) => void
  attributes: IVehicleAttribute[]
  isLoading: boolean
  slotBottomButtons?: React.ReactNode
}

const SelectionSteps: React.FC<SelectionStepProps> = ({
  currentStep,
  brands,
  onBrandSelect,
  models,
  onModelSelect,
  attributes,
  isLoading,
  slotBottomButtons
}) => {
  if (currentStep === StepOption.BRAND) {
    return isLoading ? <BrandLoader /> : <BrandCardList brands={brands} onBrandSelect={onBrandSelect} />
  }
  if (currentStep === StepOption.MODEL) {
    return isLoading ? <ModelChipsLoader /> : <ModelChips models={models} onModelSelect={onModelSelect} />
  }
  if (currentStep === StepOption.TYPE) {
    return isLoading ? <TypeSelectLoader /> : <MDMDetailsSelection attributes={attributes} />
  }
  if (currentStep === StepOption.CONFIRM) {
    return <Confirm slotBottomButtons={slotBottomButtons} />
  }
  return null
}

const MDMDetailsSelection: React.FC<{ attributes: IVehicleAttribute[] }> = ({ attributes }) => {
  const dispatch = useDispatch()
  const { locale } = useContext(LocaleContext)
  const { setType } = useSelectionContext()
  const { setFieldValue } = useFormikContext()
  const [{ value: country }] = useField('Country')

  const [year, setYear] = useState('')
  const { data: mdmYears } = useGetMDMDetails(country, true, attributes)

  const yearOptions = useMemo(() => mdmYears?.Attributes.filter(attr => attr.Name === 'VehicleYear'), [mdmYears])
  const selectedYear = useMemo(() => yearOptions?.find(attr => attr.Name === 'VehicleYear' && attr.Value === year), [mdmYears, year])

  const { data: mdmFuels } = useGetMDMDetails(country, !!selectedYear, [...attributes, selectedYear!])
  const fuelOptions = useMemo(() => mdmFuels?.Attributes.filter(attr => attr.Name === 'Fuel'), [mdmFuels])

  const onFuelSelect = async (fuel: string) => {
    const selectedFuel = fuelOptions?.find(attr => attr.Name === 'Fuel' && attr.Value === fuel)
    const { Data } = await dispatch(loadVehicleDetails(locale, country, [...attributes, selectedYear!, selectedFuel!]))

    setType({ key: 'key', value: 'value', name: `${selectedYear?.DisplayValue} ${selectedFuel?.DisplayValue}` })

    setFieldValue('vehicle', Data.Vehicle)
  }

  return (
    <>
      {!yearOptions && <Skeleton variant='rounded' animation='wave' height={40} />}
      {yearOptions?.length && (
        <FormControl fullWidth size='small' sx={{ mb: 2 }}>
          <InputLabel id={`${yearOptions[0].Name}_${yearOptions[0].Source}`}>
            {yearOptions[0].DisplayName || yearOptions[0].Name}
          </InputLabel>
          <Select
            value={year}
            onChange={e => {
              setYear(e.target.value || '')
            }}
            labelId={`${yearOptions[0].Name}_${yearOptions[0].Source}`}
            label={yearOptions[0].DisplayName || yearOptions[0].Name}
            data-testid='unknown-vehicle-mdm-vehicle-year'
          >
            {yearOptions?.map(option => (
              <MenuItem key={option.Value} value={option.Value}>
                {option.DisplayValue}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
      {yearOptions && selectedYear && !fuelOptions && <Skeleton variant='rounded' animation='wave' height={40} />}
      {fuelOptions?.length && (
        <FormControl fullWidth size='small'>
          <InputLabel id={`${fuelOptions[0].Name}_${fuelOptions[0].Source}`}>
            {fuelOptions[0].DisplayName || fuelOptions[0].Name}
          </InputLabel>
          <Select
            value=''
            onChange={e => {
              onFuelSelect(e.target.value || '')
            }}
            labelId={`${fuelOptions[0].Name}_${fuelOptions[0].Source}`}
            label={fuelOptions[0].DisplayName || fuelOptions[0].Name}
            data-testid='unknown-vehicle-mdm-fuel'
          >
            {fuelOptions?.map(option => (
              <MenuItem key={option.Value} value={option.Value}>
                {option.DisplayValue}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
    </>
  )
}
